import { combineParameters, normalizeInputTypes, composeStepRunners, sortStoriesV6, userOrAutoTitle } from './chunk-UUHU5F55.mjs';
import { dedent } from 'ts-dedent';
import { global } from '@storybook/global';
import { logger } from '@storybook/client-logger';
import { sanitize, toId, isExportStory, storyNameFromExport } from '@storybook/csf';
import { SynchronousPromise } from 'synchronous-promise';
import { parse } from 'qs';

var AUTODOCS_TAG = "autodocs";
var STORIES_MDX_TAG = "stories-mdx";
var StoryStoreFacade = class {
  constructor() {
    this.projectAnnotations = {
      loaders: [],
      decorators: [],
      parameters: {},
      argsEnhancers: [],
      argTypesEnhancers: [],
      args: {},
      argTypes: {}
    };
    this.entries = {};
    this.csfExports = {};
  }
  // This doesn't actually import anything because the client-api loads fully
  // on startup, but this is a shim after all.
  importFn(path) {
    return SynchronousPromise.resolve().then(() => {
      const moduleExports = this.csfExports[path];
      if (!moduleExports)
        throw new Error(`Unknown path: ${path}`);
      return moduleExports;
    });
  }
  getStoryIndex(store) {
    const fileNameOrder = Object.keys(this.csfExports);
    const storySortParameter = this.projectAnnotations.parameters?.options?.storySort;
    const storyEntries = Object.entries(this.entries);
    const sortableV6 = storyEntries.map(([storyId, { type, importPath, ...entry }]) => {
      const exports = this.csfExports[importPath];
      const csfFile = store.processCSFFileWithCache(
        exports,
        importPath,
        exports.default.title
      );
      let storyLike;
      if (type === "story") {
        storyLike = store.storyFromCSFFile({ storyId, csfFile });
      } else {
        storyLike = {
          ...entry,
          story: entry.name,
          kind: entry.title,
          componentId: toId(entry.componentId || entry.title),
          parameters: { fileName: importPath }
        };
      }
      return [
        storyId,
        storyLike,
        csfFile.meta.parameters,
        this.projectAnnotations.parameters || {}
      ];
    });
    let sortedV7;
    try {
      sortedV7 = sortStoriesV6(sortableV6, storySortParameter, fileNameOrder);
    } catch (err) {
      if (typeof storySortParameter === "function") {
        throw new Error(dedent`
          Error sorting stories with sort parameter ${storySortParameter}:

          > ${err.message}
          
          Are you using a V7-style sort function in V6 compatibility mode?
          
          More info: https://github.com/storybookjs/storybook/blob/next/MIGRATION.md#v7-style-story-sort
        `);
      }
      throw err;
    }
    const entries = sortedV7.reduce((acc, s) => {
      acc[s.id] = this.entries[s.id];
      return acc;
    }, {});
    return { v: 4, entries };
  }
  clearFilenameExports(fileName) {
    if (!this.csfExports[fileName]) {
      return;
    }
    Object.entries(this.entries).forEach(([id, { importPath }]) => {
      if (importPath === fileName) {
        delete this.entries[id];
      }
    });
    this.csfExports[fileName] = {};
  }
  // NOTE: we could potentially share some of this code with the stories.json generation
  addStoriesFromExports(fileName, fileExports) {
    if (fileName.match(/\.mdx$/) && !fileName.match(/\.stories\.mdx$/)) {
      if (global.FEATURES?.storyStoreV7MdxErrors !== false) {
        throw new Error(dedent`
        Cannot index \`.mdx\` file (\`${fileName}\`) in \`storyStoreV7: false\` mode.

        The legacy story store does not support new-style \`.mdx\` files. If the file above
        is not intended to be indexed (i.e. displayed as an entry in the sidebar), either
        exclude it from your \`stories\` glob, or add <Meta isTemplate /> to it.
        
        If you wanted to index the file, you'll need to name it \`stories.mdx\` and stick to the
        legacy (6.x) MDX API, or use the new store.`);
      }
    }
    if (this.csfExports[fileName] === fileExports) {
      return;
    }
    this.clearFilenameExports(fileName);
    const { default: defaultExport, __namedExportsOrder, ...namedExports } = fileExports;
    let { id: componentId, title, tags: componentTags = [] } = defaultExport || {};
    const specifiers = (global.STORIES || []).map(
      (specifier) => ({
        ...specifier,
        importPathMatcher: new RegExp(specifier.importPathMatcher)
      })
    );
    title = userOrAutoTitle(fileName, specifiers, title);
    if (!title) {
      logger.info(
        `Unexpected default export without title in '${fileName}': ${JSON.stringify(
          fileExports.default
        )}`
      );
      return;
    }
    this.csfExports[fileName] = {
      ...fileExports,
      default: { ...defaultExport, title }
    };
    let sortedExports = namedExports;
    if (Array.isArray(__namedExportsOrder)) {
      sortedExports = {};
      __namedExportsOrder.forEach((name) => {
        const namedExport = namedExports[name];
        if (namedExport)
          sortedExports[name] = namedExport;
      });
    }
    const storyExports = Object.entries(sortedExports).filter(
      ([key]) => isExportStory(key, defaultExport)
    );
    const docsOptions = global.DOCS_OPTIONS || {};
    const { autodocs } = docsOptions;
    const componentAutodocs = componentTags.includes(AUTODOCS_TAG);
    const autodocsOptedIn = autodocs === true || autodocs === "tag" && componentAutodocs;
    if (storyExports.length) {
      if (componentTags.includes(STORIES_MDX_TAG) || autodocsOptedIn) {
        const name = docsOptions.defaultName;
        const docsId = toId(componentId || title, name);
        this.entries[docsId] = {
          type: "docs",
          id: docsId,
          title,
          name,
          importPath: fileName,
          ...componentId && { componentId },
          tags: [
            ...componentTags,
            "docs",
            ...autodocsOptedIn && !componentAutodocs ? [AUTODOCS_TAG] : []
          ],
          storiesImports: []
        };
      }
    }
    storyExports.forEach(([key, storyExport]) => {
      const exportName = storyNameFromExport(key);
      const id = storyExport.parameters?.__id || toId(componentId || title, exportName);
      const name = typeof storyExport !== "function" && storyExport.name || storyExport.storyName || storyExport.story?.name || exportName;
      if (!storyExport.parameters?.docsOnly) {
        this.entries[id] = {
          type: "story",
          id,
          name,
          title,
          importPath: fileName,
          ...componentId && { componentId },
          tags: [...storyExport.tags || componentTags, "story"]
        };
      }
    });
  }
};

// src/modules/client-api/ClientApi.ts
var warningAlternatives = {
  addDecorator: `Instead, use \`export const decorators = [];\` in your \`preview.js\`.`,
  addParameters: `Instead, use \`export const parameters = {};\` in your \`preview.js\`.`,
  addLoader: `Instead, use \`export const loaders = [];\` in your \`preview.js\`.`,
  addArgs: "",
  addArgTypes: "",
  addArgsEnhancer: "",
  addArgTypesEnhancer: "",
  addStepRunner: "",
  getGlobalRender: "",
  setGlobalRender: ""
};
var checkMethod = (method) => {
  if (global.FEATURES?.storyStoreV7) {
    throw new Error(
      dedent`You cannot use \`${method}\` with the new Story Store.

      ${warningAlternatives[method]}`
    );
  }
  if (!global.__STORYBOOK_CLIENT_API__) {
    throw new Error(`Singleton client API not yet initialized, cannot call \`${method}\`.`);
  }
};
var addDecorator = (decorator) => {
  checkMethod("addDecorator");
  global.__STORYBOOK_CLIENT_API__?.addDecorator(decorator);
};
var addParameters = (parameters) => {
  checkMethod("addParameters");
  global.__STORYBOOK_CLIENT_API__?.addParameters(parameters);
};
var addLoader = (loader) => {
  checkMethod("addLoader");
  global.__STORYBOOK_CLIENT_API__?.addLoader(loader);
};
var addArgs = (args) => {
  checkMethod("addArgs");
  global.__STORYBOOK_CLIENT_API__?.addArgs(args);
};
var addArgTypes = (argTypes) => {
  checkMethod("addArgTypes");
  global.__STORYBOOK_CLIENT_API__?.addArgTypes(argTypes);
};
var addArgsEnhancer = (enhancer) => {
  checkMethod("addArgsEnhancer");
  global.__STORYBOOK_CLIENT_API__?.addArgsEnhancer(enhancer);
};
var addArgTypesEnhancer = (enhancer) => {
  checkMethod("addArgTypesEnhancer");
  global.__STORYBOOK_CLIENT_API__?.addArgTypesEnhancer(enhancer);
};
var addStepRunner = (stepRunner) => {
  checkMethod("addStepRunner");
  global.__STORYBOOK_CLIENT_API__?.addStepRunner(stepRunner);
};
var setGlobalRender = (render) => {
  checkMethod("setGlobalRender");
  if (global.__STORYBOOK_CLIENT_API__) {
    global.__STORYBOOK_CLIENT_API__.facade.projectAnnotations.render = render;
  }
};
var invalidStoryTypes = /* @__PURE__ */ new Set(["string", "number", "boolean", "symbol"]);
var ClientApi = class {
  constructor({ storyStore } = {}) {
    // If we don't get passed modules so don't know filenames, we can
    // just use numeric indexes
    this.lastFileName = 0;
    this.addDecorator = (decorator) => {
      this.facade.projectAnnotations.decorators?.push(decorator);
    };
    this.addParameters = ({
      globals,
      globalTypes,
      ...parameters
    }) => {
      this.facade.projectAnnotations.parameters = combineParameters(
        this.facade.projectAnnotations.parameters,
        parameters
      );
      if (globals) {
        this.facade.projectAnnotations.globals = {
          ...this.facade.projectAnnotations.globals,
          ...globals
        };
      }
      if (globalTypes) {
        this.facade.projectAnnotations.globalTypes = {
          ...this.facade.projectAnnotations.globalTypes,
          ...normalizeInputTypes(globalTypes)
        };
      }
    };
    this.addStepRunner = (stepRunner) => {
      this.facade.projectAnnotations.runStep = composeStepRunners(
        [this.facade.projectAnnotations.runStep, stepRunner].filter(Boolean)
      );
    };
    this.addLoader = (loader) => {
      this.facade.projectAnnotations.loaders?.push(loader);
    };
    this.addArgs = (args) => {
      this.facade.projectAnnotations.args = {
        ...this.facade.projectAnnotations.args,
        ...args
      };
    };
    this.addArgTypes = (argTypes) => {
      this.facade.projectAnnotations.argTypes = {
        ...this.facade.projectAnnotations.argTypes,
        ...normalizeInputTypes(argTypes)
      };
    };
    this.addArgsEnhancer = (enhancer) => {
      this.facade.projectAnnotations.argsEnhancers?.push(enhancer);
    };
    this.addArgTypesEnhancer = (enhancer) => {
      this.facade.projectAnnotations.argTypesEnhancers?.push(enhancer);
    };
    // Because of the API of `storiesOf().add()` we don't have a good "end" call for a
    // storiesOf file to finish adding stories, and us to load it into the facade as a
    // single psuedo-CSF file. So instead we just keep collecting the CSF files and load
    // them all into the facade at the end.
    this._addedExports = {};
    // what are the occasions that "m" is a boolean vs an obj
    this.storiesOf = (kind, m) => {
      if (!kind && typeof kind !== "string") {
        throw new Error("Invalid or missing kind provided for stories, should be a string");
      }
      if (!m) {
        logger.warn(
          `Missing 'module' parameter for story with a kind of '${kind}'. It will break your HMR`
        );
      }
      if (m) {
        const proto = Object.getPrototypeOf(m);
        if (proto.exports && proto.exports.default) {
          logger.error(
            `Illegal mix of CSF default export and storiesOf calls in a single file: ${proto.i}`
          );
        }
      }
      const baseFilename = m && m.id ? `${m.id}` : (this.lastFileName++).toString();
      let fileName = baseFilename;
      let i = 1;
      while (this._addedExports[fileName]) {
        i += 1;
        fileName = `${baseFilename}-${i}`;
      }
      if (m && m.hot && m.hot.accept) {
        m.hot.accept();
        m.hot.dispose(() => {
          this.facade.clearFilenameExports(fileName);
          delete this._addedExports[fileName];
          setTimeout(() => {
            this._loadAddedExports();
            this.onImportFnChanged?.({ importFn: this.importFn.bind(this) });
          }, 0);
        });
      }
      let hasAdded = false;
      const api = {
        kind: kind.toString(),
        add: () => api,
        addDecorator: () => api,
        addLoader: () => api,
        addParameters: () => api
      };
      Object.keys(this.addons).forEach((name) => {
        const addon = this.addons[name];
        api[name] = (...args) => {
          addon.apply(api, args);
          return api;
        };
      });
      const meta = {
        id: sanitize(kind),
        title: kind,
        decorators: [],
        loaders: [],
        parameters: {}
      };
      this._addedExports[fileName] = { default: meta };
      let counter = 0;
      api.add = (storyName, storyFn, parameters = {}) => {
        hasAdded = true;
        if (typeof storyName !== "string") {
          throw new Error(`Invalid or missing storyName provided for a "${kind}" story.`);
        }
        if (!storyFn || Array.isArray(storyFn) || invalidStoryTypes.has(typeof storyFn)) {
          throw new Error(
            `Cannot load story "${storyName}" in "${kind}" due to invalid format. Storybook expected a function/object but received ${typeof storyFn} instead.`
          );
        }
        const { decorators, loaders, component, args, argTypes, ...storyParameters } = parameters;
        const storyId = parameters.__id || toId(kind, storyName);
        const csfExports = this._addedExports[fileName];
        csfExports[`story${counter}`] = {
          name: storyName,
          parameters: { fileName, __id: storyId, ...storyParameters },
          decorators,
          loaders,
          args,
          argTypes,
          component,
          render: storyFn
        };
        counter += 1;
        return api;
      };
      api.addDecorator = (decorator) => {
        if (hasAdded)
          throw new Error(`You cannot add a decorator after the first story for a kind.
Read more here: https://github.com/storybookjs/storybook/blob/master/MIGRATION.md#can-no-longer-add-decoratorsparameters-after-stories`);
        meta.decorators?.push(decorator);
        return api;
      };
      api.addLoader = (loader) => {
        if (hasAdded)
          throw new Error(`You cannot add a loader after the first story for a kind.`);
        meta.loaders?.push(loader);
        return api;
      };
      api.addParameters = ({ component, args, argTypes, tags, ...parameters }) => {
        if (hasAdded)
          throw new Error(`You cannot add parameters after the first story for a kind.
Read more here: https://github.com/storybookjs/storybook/blob/master/MIGRATION.md#can-no-longer-add-decoratorsparameters-after-stories`);
        meta.parameters = combineParameters(meta.parameters, parameters);
        if (component)
          meta.component = component;
        if (args)
          meta.args = { ...meta.args, ...args };
        if (argTypes)
          meta.argTypes = { ...meta.argTypes, ...argTypes };
        if (tags)
          meta.tags = tags;
        return api;
      };
      return api;
    };
    // @deprecated
    this.raw = () => {
      return this.storyStore?.raw();
    };
    this.facade = new StoryStoreFacade();
    this.addons = {};
    this.storyStore = storyStore;
  }
  importFn(path) {
    return this.facade.importFn(path);
  }
  getStoryIndex() {
    if (!this.storyStore) {
      throw new Error("Cannot get story index before setting storyStore");
    }
    return this.facade.getStoryIndex(this.storyStore);
  }
  _loadAddedExports() {
    Object.entries(this._addedExports).forEach(
      ([fileName, fileExports]) => this.facade.addStoriesFromExports(fileName, fileExports)
    );
  }
  // @deprecated
  get _storyStore() {
    return this.storyStore;
  }
};
var getQueryParams = () => {
  const { document } = global;
  if (document && document.location && document.location.search) {
    return parse(document.location.search, { ignoreQueryPrefix: true });
  }
  return {};
};
var getQueryParam = (key) => {
  const params = getQueryParams();
  return params[key];
};

export { ClientApi, addArgTypes, addArgTypesEnhancer, addArgs, addArgsEnhancer, addDecorator, addLoader, addParameters, addStepRunner, getQueryParam, getQueryParams, setGlobalRender };
